import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, Lock, Loader2, Sparkles, FileText, Download, Play, Copy,
  Rocket, CalendarClock, CircleDot, MessageSquare, ListChecks, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { DeploymentCenter } from "../components/DeploymentCenter";
import { useAuth } from "../hooks/useAuth";
import { fetchMyContinueBuilding, fetchCBPlan, subscribeContinueBuilding } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return v;
  }
};

export default function ContinueBuilding() {
  const navigate = useNavigate();
  const { user, loading: authLoading, startLogin } = useAuth();
  const [project, setProject] = useState(undefined); // undefined = loading, null = none
  const [plan, setPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setProject(null);
      fetchCBPlan().then(setPlan).catch(() => {});
      return;
    }
    fetchMyContinueBuilding()
      .then((p) => {
        setProject(p || null);
        if (!p) fetchCBPlan().then(setPlan).catch(() => {});
      })
      .catch(() => setProject(null));
    window.scrollTo(0, 0);
  }, [user, authLoading]);

  const subscribe = async () => {
    if (!user) {
      startLogin();
      return;
    }
    setSubscribing(true);
    try {
      const { url } = await subscribeContinueBuilding();
      window.location.href = url;
      setTimeout(() => setSubscribing(false), 6000);
    } catch {
      toast.error("Could not start checkout", { description: "Please try again in a moment." });
      setSubscribing(false);
    }
  };

  if (project === undefined) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-24 flex justify-center">
          <Loader2 className="animate-spin text-[#D4AF37]" size={30} />
        </div>
      </Layout>
    );
  }

  // ---- No subscription yet → sales / subscribe view ----
  if (!project) return <SubscribeView plan={plan} onSubscribe={subscribe} subscribing={subscribing} navigate={navigate} />;

  // ---- Active build dashboard ----
  const phases = [...(project.phases || [])].sort((a, b) => a.order - b.order);
  const total = phases.length;
  const completed = phases.filter((p) => p.status === "completed").length;
  const currentPhase = phases.find((p) => p.status === "current");
  const currentIdx = phases.findIndex((p) => p.status === "current");
  const nextPhase = currentIdx >= 0 ? phases[currentIdx + 1] : null;
  const remaining = total - completed;
  const pct = project.progress_percentage ?? 0;

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-12" data-testid="continue-building-page">
        <button onClick={() => navigate("/profile")} className="inline-flex items-center gap-1.5 text-sm text-[#687280] hover:text-[#0F0F0F] mb-6">
          <ArrowLeft size={16} /> Profile
        </button>

        {/* Hero */}
        <div className="bg-[#0F0F0F] rounded-3xl p-8 text-white">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${project.subscription_status === "active" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-[#A1A1AA]"}`} data-testid="cb-sub-status">
                <CircleDot size={12} /> Subscription {project.subscription_status}
              </span>
              <h1 className="font-display text-3xl font-extrabold mt-3" data-testid="cb-project-name">{project.project_name}</h1>
              <p className="text-[#A1A1AA] text-sm mt-1">Continue Building Program</p>
            </div>
            <div className="text-right">
              <p className="text-[#A1A1AA] text-xs flex items-center gap-1.5 justify-end"><CalendarClock size={13} /> Next billing</p>
              <p className="font-display text-lg font-bold">{fmtDate(project.renewal_date)}</p>
              <p className="text-xs text-[#D4AF37] mt-0.5">${project.monthly_price}/month</p>
            </div>
          </div>

          <div className="mt-7">
            <div className="flex items-center justify-between text-xs text-[#A1A1AA] mb-1.5">
              <span>Project progress</span>
              <span className="font-semibold text-white" data-testid="cb-progress">{pct}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-7">
            <MiniStat label="Current phase" value={currentPhase?.name || "—"} />
            <MiniStat label="Next phase" value={nextPhase?.name || (remaining <= 0 ? "Complete 🎉" : "—")} />
            <MiniStat label="Phases remaining" value={`${remaining} of ${total}`} />
          </div>
        </div>

        {/* Phases */}
        <h2 className="font-display text-2xl font-bold text-[#0F0F0F] mt-10 mb-5">Build Phases</h2>
        <div className="space-y-4" data-testid="cb-phases">
          {phases.map((p, i) => (
            <PhaseCard key={p.id} phase={p} index={i} project={project} onUpdated={setProject} />
          ))}
        </div>

        {/* Builder resources */}
        <h2 className="font-display text-2xl font-bold text-[#0F0F0F] mt-12 mb-5">Builder Resources</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ResourceTile icon={Sparkles} title="AI Build Coach" desc="Ask questions about your project and current phase." badge="Coming Soon" muted testid="cb-ai-coach" />
          <ResourceTile icon={MessageSquare} title="Messages" desc="Chat with the LaunchBS build team." badge="Coming Soon" muted testid="cb-messages" />
        </div>
      </section>
    </Layout>
  );
}

const MiniStat = ({ label, value }) => (
  <div className="bg-white/5 rounded-xl p-3">
    <p className="text-[10px] uppercase tracking-wide text-[#71717A]">{label}</p>
    <p className="font-semibold text-sm mt-0.5 truncate">{value}</p>
  </div>
);

const PhaseCard = ({ phase, index, project, onUpdated }) => {
  const locked = phase.status === "locked";
  const done = phase.status === "completed";
  const current = phase.status === "current";
  const isDeployment = phase.phase_type === "deployment";

  const copyPrompt = async () => {
    const ok = await copyToClipboard(phase.builder_prompt || "");
    if (ok) toast.success("Builder prompt copied");
    else toast.error("Could not copy. Please copy manually.");
  };

  return (
    <div
      className={`rounded-2xl border p-6 transition-all ${
        current ? "border-[#D4AF37] bg-[#FFFDF5] shadow-[0_10px_30px_rgba(212,175,55,0.12)]" : locked ? "border-gray-100 bg-[#FAFAFA]" : "border-gray-100 bg-white"
      }`}
      data-testid={`cb-phase-${index + 1}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold ${
          done ? "bg-[#D4AF37] text-[#0F0F0F]" : current ? "bg-[#0F0F0F] text-[#D4AF37]" : "bg-[#F0F0F0] text-[#9CA3AF]"
        }`}>
          {done ? <Check size={18} /> : locked ? <Lock size={16} /> : index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-[#0F0F0F]">Phase {index + 1}: {phase.name}</h3>
            {current && <span className="text-[10px] font-bold uppercase tracking-wide text-[#D4AF37] bg-[#D4AF37]/10 rounded-full px-2 py-0.5">In progress</span>}
            {done && <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">Completed</span>}
            {locked && <span className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] bg-[#F0F0F0] rounded-full px-2 py-0.5">Locked</span>}
          </div>
          {phase.description && <p className="text-sm text-[#687280] mt-1">{phase.description}</p>}

          {phase.includes?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {phase.includes.map((inc) => (
                <span key={inc} className="text-xs bg-[#F7F7F7] text-[#687280] rounded-full px-2.5 py-1">{inc}</span>
              ))}
            </div>
          )}

          {locked ? (
            <p className="text-xs text-[#9CA3AF] mt-4 inline-flex items-center gap-1.5">
              <Lock size={12} /> Unlocks when your current phase is released.
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {isDeployment && <DeploymentCenter project={project} onUpdated={onUpdated} />}
              {phase.builder_prompt && (
                <div className="rounded-xl bg-[#0F0F0F] p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-[#D4AF37]">AI Builder Prompt</p>
                    <button onClick={copyPrompt} className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white" data-testid={`cb-copy-prompt-${index + 1}`}>
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <p className="text-xs text-[#A1A1AA] leading-relaxed line-clamp-4 whitespace-pre-wrap">{phase.builder_prompt}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {phase.documentation_url && <ResBtn icon={FileText} label="Documentation" href={phase.documentation_url} />}
                {phase.project_files_url && <ResBtn icon={Download} label="Project Files" href={phase.project_files_url} />}
                {phase.video_url && <ResBtn icon={Play} label="Walkthrough Video" href={phase.video_url} />}
                {(phase.resources || []).map((r, ri) => r?.url && <ResBtn key={ri} icon={ArrowRight} label={r.label || "Resource"} href={r.url} />)}
              </div>
              {phase.checklist?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#0F0F0F] mb-1.5 inline-flex items-center gap-1.5"><ListChecks size={13} /> Build checklist</p>
                  <ul className="space-y-1">
                    {phase.checklist.map((c, ci) => (
                      <li key={ci} className="text-sm text-[#687280] flex items-center gap-2"><Check size={13} className="text-[#D4AF37]" /> {c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {phase.next_steps && (
                <p className="text-sm text-[#0F0F0F] bg-[#F7F7F7] rounded-xl p-3"><span className="font-semibold">Next steps: </span>{phase.next_steps}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResBtn = ({ icon: Icon, label, href }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-medium text-[#0F0F0F] hover:bg-[#F7F7F7] transition-colors">
    <Icon size={13} className="text-[#D4AF37]" /> {label}
  </a>
);

const ResourceTile = ({ icon: Icon, title, desc, badge, muted, testid }) => (
  <div className={`rounded-2xl border border-gray-100 p-6 ${muted ? "bg-[#FAFAFA]" : "bg-white"}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center"><Icon size={18} /></div>
      {badge && <span className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] bg-[#F0F0F0] rounded-full px-2 py-0.5">{badge}</span>}
    </div>
    <p className="font-display font-semibold text-[#0F0F0F]">{title}</p>
    <p className="text-sm text-[#687280] mt-1">{desc}</p>
  </div>
);

const SubscribeView = ({ plan, onSubscribe, subscribing, navigate }) => (
  <Layout>
    <section className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid="cb-subscribe-view">
      <button onClick={() => navigate("/profile")} className="inline-flex items-center gap-1.5 text-sm text-[#687280] hover:text-[#0F0F0F] mb-6">
        <ArrowLeft size={16} /> Profile
      </button>
      <div className="bg-[#0F0F0F] rounded-3xl p-10 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 50% 15%, rgba(212,175,55,0.25) 0%, rgba(212,175,55,0) 55%)" }} />
        <div className="relative">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5"><Rocket className="text-[#D4AF37]" size={26} /></div>
          <h1 className="font-display text-3xl font-extrabold">Continue Building Program</h1>
          <p className="text-[#A1A1AA] mt-3 max-w-lg mx-auto">
            Build your application one guided phase at a time. Each month we release a new build phase with an AI builder prompt, documentation, project files, and a step-by-step checklist — so you always know exactly what to build next.
          </p>
          <p className="font-display text-4xl font-extrabold mt-6">${plan?.monthly_price ?? 149}<span className="text-lg text-[#A1A1AA] font-medium">/month</span></p>
          <button
            onClick={onSubscribe}
            disabled={subscribing}
            className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
            data-testid="cb-subscribe-btn"
          >
            {subscribing ? "Redirecting to checkout…" : <>Start Continue Building <ArrowRight size={18} /></>}
          </button>
          <p className="text-xs text-[#687280] mt-3">Cancel anytime · Powered by Stripe</p>
        </div>
      </div>

      {plan?.phases?.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-xl font-bold text-[#0F0F0F] mb-5">What you'll build, phase by phase</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plan.phases.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`cb-plan-phase-${i + 1}`}>
                <p className="text-xs font-semibold text-[#D4AF37]">Phase {i + 1}</p>
                <p className="font-display font-bold text-[#0F0F0F] mt-0.5">{p.name}</p>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {(p.includes || []).map((inc) => (
                    <span key={inc} className="text-xs bg-[#F7F7F7] text-[#687280] rounded-full px-2.5 py-1">{inc}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  </Layout>
);
